import React, {
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
  useMemo,
} from "react";
import axios from "axios";
import Layout from "../../layout/Layout";
import { config, payslipAPI, baseURL } from "../../config";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Grid,
  Button,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import dayjs from "dayjs";
import * as XLSX from "xlsx/xlsx.mjs";
import moment from "moment/moment";
import { AlertMessage } from "../../components/AlertMessage";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";

import { MaterialReactTable } from "material-react-table";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useTheme } from "@emotion/react";

function PayslipReport() {
  const childRef = useRef();
  const navigate = useNavigate();

  const [fromDate, setFromDate] = React.useState(
    dayjs(new Date()).format("YYYY-MM")
  );

  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [payslipReportData, setPayslipReportData] = useState([]);
  const [totalNetPay, setTotalNetPay] = useState("");
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [filters, setFilters] = useState([]);
  const [activeBatchEmployees, setActiveBatchEmployees] = useState([]);
  const theme = useTheme();

  // Export columns selection state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState({
    employee_name: true,
    employee_no: true,
    basic_pay: true,
    no_of_days_leave_taken: true,
    salary_advance: true,
    personal_advance: true,
    total_deductions: true,
    net_pay: true,
    gross_pay: true,
    epf_fund: true,
    rd_esi: true,
    professional_tax: true,
    tds: true,
  });

  const exportColumnLabels = {
    employee_name: "Employee Name (Compulsory)",
    employee_no: "Employee No (Compulsory)",
    basic_pay: "Basic Pay",
    no_of_days_leave_taken: "Leave Taken",
    salary_advance: "Salary Advance",
    personal_advance: "Personal Advance",
    total_deductions: "Total Deductions",
    net_pay: "Net Pay",
    gross_pay: "Gross Pay",
    epf_fund: "EPF Fund",
    rd_esi: "RD ESI",
    professional_tax: "Professional Tax",
    tds: "TDS",
  };

  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const handleEditClick = (data) => {
    navigate(`/payslip/${data.employee_id}/${data.payslip_date}`);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "employee_name",
        header: "Employee Name",
        enableSorting: true,
        enableFiltering: true,
        size: 200,
        Cell: ({ cell, row }) =>
          `${row.original.employee_name} (${row.original.employee_no})`,
      },
      {
        accessorKey: "no_of_days_leave_taken",
        header: "Leave",
        enableSorting: true,
        enableFiltering: true,
        size: 50,
      },
      {
        accessorKey: "salary_advance",
        header: "Salary Advance",
        enableSorting: true,
        enableFiltering: true,
        size: 50,
      },
      {
        accessorKey: "personal_advance",
        header: "Personal Advance",
        enableSorting: true,
        enableFiltering: true,
        size: 70,
      },
      {
        accessorKey: "total_deductions",
        header: "Total Deductions",
        enableSorting: false,
        enableFiltering: true,
        size: 100,
      },
      {
        accessorKey: "basic_pay",
        header: "Basic Pay",
        enableSorting: true,
        enableFiltering: true,
        size: 100,
      },
      {
        accessorKey: "net_pay",
        header: "Net Pay",
        enableSorting: true,
        enableFiltering: true,
        size: 100,
      },

      {
        accessorKey: "ID",
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <IconButton
            color="warning"
            onClick={() => handleEditClick(row.original)}
          >
            <VisibilityIcon />
          </IconButton>
        ),
        size: 50,
      },
    ],
    [navigate]
  );
  useEffect(() => {
    fetchData();
  }, [fromDate]);

  const fetchData = async (date) => {
    setLoading(true);
    const targetDate = date ? date : fromDate;
    
    // Check if payroll batch is frozen (Locked or Paid) for target month.
    // We can check payroll batch lists or directly filter the results.
    // Let's call the list batches endpoint to verify.
    // If not locked/paid, do not set data.
    try {
      const userObj = JSON.parse(localStorage.getItem("user") || "{}");
      const companyRes = await axios({
        ...config,
        method: "POST",
        url: `${baseURL}/manage-companies`,
        data: JSON.stringify({ intType: 5 }),
      });
      const targetCompany = companyRes.data.result?.[0]?.id;
      
      let allowedEmpIds = null;
      if (targetCompany) {
        const batchRes = await axios({
          method: "GET",
          url: `${baseURL}/payroll/list/${targetCompany}`,
        });
        const batchList = batchRes.data.result || [];
        const activeBatch = batchList.find((b) => b.period_month_year === targetDate);
        
        if (!activeBatch || (activeBatch.status !== "Locked" && activeBatch.status !== "Paid")) {
          setPayslipReportData([]);
          setTotalCount(0);
          setTotalNetPay("");
          setLoading(false);
          setActiveBatchEmployees([]);
          return;
        } else {
          const empRes = await axios({
            method: "GET",
            url: `${baseURL}/payroll/batch/${activeBatch.id}/employees`,
          });
          const employeesList = empRes.data.result || [];
          setActiveBatchEmployees(employeesList);
          allowedEmpIds = new Set(
            employeesList
              .filter((emp) => emp.status !== "Hold" && emp.status !== "Stopped")
              .map((emp) => emp.employee_id)
          );
        }
      }

      const data = {
        intType: 3,
        txtPayslipFromDate: targetDate,
        intLimit: 10000,
        intOffset: 0,
        txtField: [],
        txtOrderBy: [],
      };

      config.method = "POST";
      config.data = JSON.stringify(data);
      config.url = payslipAPI;

      await axios(config)
        .then(function (response) {
          setLoading(false);
          let filteredList = response.data.result || [];
          if (allowedEmpIds) {
            filteredList = filteredList.filter((row) => allowedEmpIds.has(row.employee_id));
          }
          
          // Calculate total net pay from the filtered list of active/approved employees
          const calculatedTotalNetPay = filteredList.reduce((sum, row) => sum + parseFloat(row.net_pay || 0), 0);
          setTotalNetPay(calculatedTotalNetPay);
          
          setPayslipReportData(filteredList);
          setTotalCount(filteredList.length);
        })
        .catch(function (error) {
          setLoading(false);
        });
    } catch (e) {
      console.error("Error verifying payroll freeze status", e);
      setLoading(false);
    }
  };

  const handleExportClick = () => {
    setExportDialogOpen(true);
  };

  const downloadExcel = () => {
    const data = JSON.stringify({
      intType: 4,
      txtPayslipFromDate: fromDate,
    });

    config.method = "POST";
    config.data = data;
    config.url = payslipAPI;

    axios(config)
      .then(function (response) {
        let rawData = response.data["result"] || [];
        
        if (activeBatchEmployees.length > 0) {
          const activeEmpIds = new Set(
            activeBatchEmployees
              .filter((emp) => emp.status !== "Hold" && emp.status !== "Stopped")
              .map((emp) => emp.employee_id)
          );
          rawData = rawData.filter((row) => activeEmpIds.has(row.employee_id));
        }

        const newData = rawData.map((row) => {
          const exportRow = {};
          // Construct row based on selection
          Object.keys(selectedExportColumns).forEach((key) => {
            if (selectedExportColumns[key]) {
              const label = exportColumnLabels[key].replace(" (Compulsory)", "");
              exportRow[label] = row[key] !== undefined ? row[key] : "";
            }
          });
          return exportRow;
        });

        const workSheet = XLSX.utils.json_to_sheet(newData);
        const workBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workBook, workSheet, "Payslips");
        //Buffer
        let buf = XLSX.write(workBook, { bookType: "xlsx", type: "buffer" });
        //Binary string
        XLSX.write(workBook, { bookType: "xlsx", type: "binary" });
        //Download
        XLSX.writeFile(
          workBook,
          `KG-PaySlip-${moment(fromDate).format("MM-YYYY")}.xlsx`
        );
        setExportDialogOpen(false);
      })
      .catch(function (error) {
        if (error.message === "Network Error") {
          localStorage.removeItem("user");
          window.location.href = window.location.origin + "/";
        }
        console.log(error);
      });
  };

  return (
    <Layout>
      <Grid container spacing={3}>
        <Grid
          textAlign="center"
          item
          className="form-lable"
          xs={12}
          lg={6}
          md={6}
        >
          <Typography className="page_title" textAlign="left">
            PaySlip Report
          </Typography>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/dashboard">
              Dashboard
            </Link>
            <Typography color="text.primary">payslip</Typography>
            <Typography color="text.primary">Report</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <div className="empty-space-40"></div>
      <Grid container className="form_design" spacing={2}>
        <Grid container xs={12} md={12} lg={12}>
          <Grid item xs={12} md={6} lg={4}></Grid>

          <Grid item xs={12} md={3} lg={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Select Month and Year"
                value={fromDate}
                views={["year", "month"]}
                inputFormat="MM/YYYY"
                maxDate={new Date()}
                // {...register("txtFromDate")}
                onChange={(newValue) => {
                  setFromDate(dayjs(newValue).format("YYYY-MM"));
                  fetchData(dayjs(newValue).format("YYYY-MM"));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    // helperText={errors.txtDob?.message}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3} lg={4}>
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              className="align_button"
              textAlign="right"
            >
              <Button
                variant="primary"
                className={`btn-primary`}
                onClick={handleExportClick}
              >
                Export to Excel
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container className="form_design" spacing={2}>
        <Grid item xs={12}>
          <MaterialReactTable
            columns={columns}
            data={payslipReportData}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            className="mat-react-tbl"
            state={{
              isLoading: loading,
              pagination: { pageIndex, pageSize },
              sorting,
              columnFilters: filters,
            }}
            initialState={{
              columnPinning: lgUp ? { right: ["net_pay", "ID"] } : {},
            }}
            onPaginationChange={(updater) => {
              const paginationState = updater({
                pageIndex,
                pageSize,
              });

              const { pageIndex: newPageIndex, pageSize: newPageSize } =
                paginationState;
              setPageIndex(newPageIndex);
              setPageSize(newPageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            muiTableBodyCellProps={{
              sx: {
                padding: "3px 8px",
                border: "1px solid #ccc",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                "& input": {
                  color: "#fff !important",
                  "&::placeholder": {
                    color: "#fff !important",
                    opacity: 1,
                  },
                },
                "& .MuiSelect-select": {
                  color: "#fff !important", // selected text color
                },
                "& .MuiSelect-icon": {
                  color: "#fff !important", // dropdown arrow color
                },
                color: "#fff",
                fontWeight: "bold",
                "& svg": {
                  color: "#fff",
                },
                "& .MuiTableSortLabel-root": {
                  color: "#495057",
                  "&.Mui-active": {
                    color: "#fff",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                    },
                  },
                },
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                borderBottom: "1px solid #ccc",
                backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
              },
            })}
            muiTableHeadCellFilterTextFieldProps={{
              sx: {
                "& input": {
                  color: "#fff !important", // Change text color
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important", // Change underline color
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important", // Hover underline color
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important", // Focused underline color
                },
              },
            }}
          />
        </Grid>
      </Grid>

      {/* EXPORT COLUMN SELECTION DIALOG */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Select Columns to Export</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {Object.keys(exportColumnLabels).map((colKey) => (
              <Grid item xs={6} key={colKey}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedExportColumns[colKey]}
                      disabled={colKey === "employee_name" || colKey === "employee_no"}
                      onChange={(e) =>
                        setSelectedExportColumns({
                          ...selectedExportColumns,
                          [colKey]: e.target.checked,
                        })
                      }
                    />
                  }
                  label={exportColumnLabels[colKey]}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={downloadExcel} variant="contained" color="primary">
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default PayslipReport;
